import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { FaUserCheck, FaUsers, FaUserLargeSlash } from "react-icons/fa6";
import { FaUserCircle } from "react-icons/fa";
import axios from "axios";
import Loader from "../Loaders/Loader";
import FilterBy from "../utils/FilterBy";
import "./styles/UsersList.css";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

// interface User {
//   user_uid: string;
//   org_uid: string;
//   first_name: string;
//   last_name: string;
//   privilege: string;
//   user_status: string;
//   user_manage_access: boolean;
//   image_uid?: string;
// }

interface User {
  user_uid: string;
  org_uid: string;
  first_name: string;
  last_name: string;
  privilege: string;
  user_role: string;
  user_status: string;
  user_manage_access: boolean;
  session_info?: {
    user_online_status?: string;
  };
  image_details?: {
    image_uid: string;
    default: boolean;
  };
}

const UsersList: React.FC = () => {
  useCheckSessionHeartBeat();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const org_uid = searchParams.get("org");

  const { totalUsers, activeUsers, inactiveUsers } = location.state || {};

  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState(
    searchParams.get("filter") || "All"
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [images, setImages] = useState<Record<string, string>>({});
  const usersPerPage = 10;

  const sessionUid = localStorage.getItem("session_uid") ?? "";
  const userUid = localStorage.getItem("user_uid") ?? "";
  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const API_URL_USER_LIST = `${process.env.REACT_APP_API_URL}/api/user-list`;

  useEffect(() => {
    if (!org_uid) {
      setError(
        "Organization ID is missing. Please navigate from the organization page."
      );
      setLoading(false);
      return;
    }

    const fetchUsers = async () => {
      try {
        const response = await axios.get(API_URL_USER_LIST, {
          headers: {
            Accept: "application/json",
            "user-uid": userUid,
            "user-type": "all",
          }, withCredentials: true,
        });

        if (response.status !== 200) {
          throw new Error("Failed to fetch users");
        }

        const allUsers: User[] = Array.isArray(response.data.data)
          ? (response.data.data as User[])
          : [];
        const orgUsers = allUsers.filter((user) => user.org_uid === org_uid);

        setUsers(orgUsers);
        setFilteredUsers(orgUsers);

        const imageUids = orgUsers
          .filter((u) => u.image_details && !u.image_details.default)
          .map((u) => u.image_details!.image_uid);

        const imageMap = await fetchImagesByUid(imageUids);
        setImages(imageMap);
      } catch (err) {
        console.error(err);
        setError("Error fetching user list");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [org_uid]);

  useEffect(() => {
    const currentPageUsers = filteredUsers.slice(
      indexOfFirstUser,
      indexOfLastUser
    );

    const imageUids = currentPageUsers
      .filter((u) => u.image_details && !u.image_details.default)
      .map((u) => u.image_details!.image_uid);

    if (imageUids.length > 0) {
      fetchImagesByUid(imageUids).then((imageMap) => {
        setImages((prev) => ({ ...prev, ...imageMap }));
      });
    }
  }, [currentPage, filteredUsers]);

  const fetchImagesByUid = async (
    imageUids: string[]
  ): Promise<Record<string, string>> => {
    // Ensure max 10 at a time
    const limitedUids = imageUids.slice(0, 10);

    if (limitedUids.length === 0) return {};

    try {
      const url = new URL(`${beAddress}/api/get-image-by-uid`);

      limitedUids.forEach((uid) =>
        url.searchParams.append("image_uid_list", uid)
      );
      url.searchParams.set("is_icon_img", "true");
      url.searchParams.set("is_feedback_image", "false");

      const res = await fetch(url.toString(), {
        headers: {
          "user-uid": userUid,
        },credentials: "include",
      });

      const data = await res.json();

      if (data.status === "Success" && Array.isArray(data.data)) {
        return Object.fromEntries(
          data.data.map((item: any) => [
            item.image_uid,
            item.default ? "" : item.image,
          ])
        );
      }
    } catch (err) {
      console.error("Failed to fetch images:", err);
    }

    return {};
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "active":
        return { color: "#16821aff", label: "Online", iconname: "checkcircle" }; // green 16821aff
      case "away":
        return { color: "#f6c70aff", label: "Away", iconname: "clockcircle" }; // yellow f6c70aff
      case "invited":
        return {
          color: "#000000",
          label: "Inactive",
          iconname: "exclamationcircle",
        }; // black
      default:
        return {
          color: "#9b9292ff",
          label: "Offline",
          iconname: "closecircle",
        }; // black
    }
  };

  // Filtering logic
  useEffect(() => {
    let updatedUsers = users;

    if (searchQuery) {
      updatedUsers = updatedUsers.filter((user) =>
        `${user.first_name} ${user.last_name}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      );
    }

    if (filterType === "Admins") {
      updatedUsers = updatedUsers.filter(
        (user) => user.privilege.toLowerCase() === "orgadmin"
      );
    } else if (filterType === "Active Users") {
      updatedUsers = updatedUsers.filter(
        (user) => user.user_status.toLowerCase() === "active"
      );
    } else if (filterType === "Inactive Users") {
      updatedUsers = updatedUsers.filter(
        (user) => user.user_status.toLowerCase() === "inactive"
      );
    }

    setFilteredUsers(updatedUsers);
    setCurrentPage(1);

    // Persist filters in URL
    setSearchParams({
      search: searchQuery,
      filter: filterType,
      org: org_uid || "",
    });
  }, [searchQuery, filterType, users, org_uid, setSearchParams]);

  // Pagination logic
  const totalPages = Math.max(
    1,
    Math.ceil(filteredUsers.length / usersPerPage)
  );
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const paginate = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleRowClick = (userUid: string) => {
    navigate(`/user-profile/${userUid}`, {
      state: { userUid },
    });
  };

  if (loading) return <Loader />;
  if (error) return <p className="error-message">{error}</p>;

  return (
    <div className="users-list-container">
      <h2 style={{ fontSize: "24px", marginBottom: "20px" }}>Users List</h2>

      {/* Stats Header */}
      <div className="stats-header">
        <div className="stat-card">
          <FaUsers /> Total Users{" "}
          <span className="stat-number">{users.length}</span>
        </div>
        <div className="stat-card">
          <FaUserCheck /> Registered{" "}
          <span className="stat-number">
            {
              filteredUsers.filter((user) => user.user_status === "active")
                .length
            }
          </span>
        </div>
        <div className="stat-card">
          <FaUserLargeSlash /> Yet to register{" "}
          <span className="stat-number">
            {
              filteredUsers.filter((user) => user.user_status !== "active")
                .length
            }
          </span>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="UserList-filter-bar">
        <input
          type="text"
          className="UserList-search-bar"
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <FilterBy
          options={["All", "Admins", "Active Users", "Inactive Users"]}
          value={filterType} // <-- this line ensures selected value is passed down
          onFilterChange={setFilterType}
          heading="Filter by"
        />
      </div>

      {/* User List Table */}
      {filteredUsers.length > 0 ? (
        <div className="users-list">
          <div className="users-list-header">
            <div className="user-head-item">Profile</div>
            <div className="user-head-item">User ID</div>
            <div className="user-head-item">Name</div>
            <div className="user-head-item">Role</div>
            <div className="user-head-item">Status</div>
          </div>

          {currentUsers.map((user) => {
            const imageUid = user.image_details?.image_uid;
            const isDefault = user.image_details?.default;

            const imageSrc =
              !isDefault && imageUid && images[imageUid]
                ? images[imageUid]
                : null;

            const showDefault = !imageSrc;

            return (
              <div
                key={user.user_uid}
                className="user-row"
                onClick={() => handleRowClick(user.user_uid)}
              >
                {/* <div className="user-cell profile-cell">
                  {showDefault ? (
                    <FaUserCircle size={30} color="#555" />
                  ) : (
                    <img
                      src={`data:image/png;base64,${imageSrc}`}
                      alt="user"
                      className="user-profile-img"
                    />
                  )}
                </div> */}
                {/* <div className="user-cell profile-cell">
                  {imageSrc ? (
                    <img
                      src={`data:image/png;base64,${imageSrc}`}
                      alt="user"
                      className="user-profile-img"
                    />
                  ) : (
                    <FaUserCircle size={30} color="#555" />
                  )}
                </div> */}

                {/* <div className="user-cell profile-cell">
                  {imageSrc ? (
                    <img
                      src={`data:image/png;base64,${imageSrc}`}
                      alt="user"
                      className="user-profile-img"
                      style={{
                        width: "34px",
                        height: "34px",
                        border: `3.2px solid ${
                          getStatusInfo(
                            user.session_info?.user_online_status || "offline"
                          ).color
                        }`,
                        borderRadius: "50%",
                      }}
                    />
                  ) : (
                    <FaUserCircle size={26} color="#fff" />
                  )}
                </div> */}
                <div className="user-cell profile-cell">
                  <div className="avatar-wrapper">
                    {imageSrc ? (
                      <img
                        src={`data:image/png;base64,${imageSrc}`}
                        alt="user"
                        className="user-profile-img"
                        style={{
                          width: "34px",
                          height: "34px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <FaUserCircle className="user-avatar" />
                    )}

                    {/* Status Dot */}
                    <span
                      className="status-dot"
                      style={{
                        backgroundColor: getStatusInfo(
                          user.session_info?.user_online_status || "offline"
                        ).color,
                      }}
                    ></span>
                  </div>
                </div>

                <div className="user-cell">{user.user_uid}</div>
                <div className="user-cell">
                  {`${user.first_name} ${user.last_name}`.trim() || "No Name"}
                </div>
                <div className="user-cell">{user.user_role}</div>
                <div className="user-cell">
                  <span className={`status-badge ${user.user_status}`}>
                    {user.user_status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="no-data-message">No users available.</p>
      )}

      {/* Pagination */}
      <div className="UserList-pagination">
        <button
          className="UserList-pagination-button"
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="page-indicator">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="UserList-pagination-button"
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage >= totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default UsersList;